import { FC } from 'react'
import { useParams } from 'react-router-dom'
import { CircularProgress, Stack } from '@mui/material'
import { useLocationAlarm } from '../../../api/apiQueries'
import { Activity } from '../../../api/models'
import { Page } from '../../../components/Page'
import { AlarmInfoBox } from './AlarmInfoBox'
import { ErtPlayer } from './ErtQA/ErtPlayer'
import { NoEngagementDataSidePanel, SidePanelSkeleton } from './EngagementSidePanel/SidePanelStates'
import { Banner, BannerType } from '../../../components/Banner/Banner'
import { useErtRecordingData } from './ErtQA/useErtRecordingData'

// Alarms have no engagement activities, so the pipeline processes an empty activity list.
const NO_ACTIVITIES: Activity[] = []

// Standalone page for the alarm detail view. Unlike the engagement modes, it isn't part of the ErtPage
// `mode` layout - it renders its own <Page> - but it shares the recording pipeline and side-panel states.
export const ErtAlarmPage: FC = () => {
  const { sid: locationId, alarmId } = useParams()
  const alarmQuery = useLocationAlarm(locationId, alarmId)
  const alarm = alarmQuery.data

  const { webRecordings, audioRecordings, processedEvents, processedActivities, isRecordingsLoading, error } = useErtRecordingData({
    id: alarmId,
    locationId,
    includeVideo: false,
    activities: NO_ACTIVITIES
  })

  const isLoading = alarmQuery.isLoading || isRecordingsLoading

  return (
    <Page
      showLocationSearchBar={false}
      headerAddOn={alarm && <AlarmInfoBox alarm={alarm} />}
      sidePanelAddOn={isLoading ? <SidePanelSkeleton /> : <NoEngagementDataSidePanel />}
      height="100%"
      width="100%"
      scrollContainerProps={{ sx: { backgroundColor: 'var(--bg-surface)', overflow: 'hidden' } }}
      maxWidthContainerProps={{ sx: { maxWidth: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } }}
    >
      {locationId && alarmId && webRecordings && audioRecordings && processedEvents && processedActivities ? (
        <ErtPlayer
          locationId={locationId}
          engagementId={alarmId}
          processedActivities={processedActivities}
          webRecordings={webRecordings}
          audioRecordings={audioRecordings}
          processedRRWebEvents={processedEvents}
          recordingStartTimestamp={processedEvents ? processedEvents.timeOfFirstEvent : undefined}
          sx={{ alignSelf: 'center', height: '100%', width: '100%', minHeight: 0 }}
        />
      ) : error ? (
        <Stack data-testid="ert-error-banner" sx={{ alignItems: 'center' }}>
          <Banner type={BannerType.Error} title={'Error'} body={error} />
        </Stack>
      ) : (
        <Stack data-testid="ert-loading-spinner" sx={{ justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Stack>
      )}
    </Page>
  )
}
